import { useState } from "react";

import { ColocaLogo } from "@/shared/components/ColocaLogo";
import useTranslations from "@/shared/lang/useTranslations";
import { useTheme } from "@/shared/theme/useTheme";

import { TwoFAPage } from "../components/2FAPage";
import { ChangePasswordPage } from "../components/ChangePasswordPage";
import { LangComponent } from "../components/LangComponent";
import { LangDropdown } from "../components/LangDropdown";
import LoginForm from "../components/LoginForm";
import { ThemeSwitch } from "../components/ThemeSwitch";
import { useAuth } from "../hooks/useAuth";
import { useTurnstile } from "../hooks/useTurnstile";

export const LoginPage = () => {
  const { theme, toggleTheme } = useTheme();
  const {
    requires2FA,
    changeRequires2FA,
    doLogin,
    isLoading,
    error,
    clearError,
    mustChangePassword,
    doChangePassword,
  } = useAuth();
  const { t, activeLanguage, setLanguage } = useTranslations("login");
  const [credentials, setCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);

  // One controller for both steps: the 2FA retry is another POST /auth/login and needs a fresh token.
  const turnstile = useTurnstile("login");

  const handleLogin = (values: { email: string; password: string }) => {
    setCredentials(values);
    doLogin({ ...values, turnstileToken: turnstile.token });
    turnstile.reset();
  };

  const handleTwoFASubmit = (code: string) => {
    if (credentials) {
      doLogin({
        email: credentials.email,
        password: credentials.password,
        twoFactorCode: code,
        turnstileToken: turnstile.token,
      });
      turnstile.reset();
    }
  };

  const handleChangePasswordSubmit = (values: { currentPassword: string; newPassword: string }) => {
    doChangePassword(values);
  };

  const handleBackToLogin = () => {
    setCredentials(null);
    clearError();
    changeRequires2FA(false);
  };

  return (
    <div className="flex w-full h-screen">
      <div className="hidden xl:block xl:w-1/2">
        <LangComponent t={t} />
      </div>
      <div className="w-full xl:w-1/2 flex justify-center items-center">
        <div className="flex flex-col h-full w-full">
          <div className="flex justify-end p-5 gap-2 animate-fade-down">
            <LangDropdown
              defaultSelected={activeLanguage}
              items={[
                { label: "EN", value: "en" },
                { label: "ES", value: "es" },
                { label: "PT", value: "pt" },
              ]}
              onChange={(value) => setLanguage(value)}
            />
            <ThemeSwitch theme={theme} toggleTheme={toggleTheme} />
          </div>
          <div className="w-full h-full flex flex-col justify-center items-center gap-12 px-5">
            <div className="md:max-w-48 max-w-40 animate-fade-down">
              <ColocaLogo variant={theme === "dark" ? "white" : "black"} />
            </div>
            {requires2FA && !mustChangePassword && (
              <TwoFAPage
                onSubmit={handleTwoFASubmit}
                onBackToLogin={handleBackToLogin}
                isLoading={!!isLoading}
                error={error || undefined}
                turnstile={turnstile}
              />
            )}
            {mustChangePassword && (
              <ChangePasswordPage
                onSubmit={handleChangePasswordSubmit}
                isLoading={!!isLoading}
                error={error || undefined}
              />
            )}
            {!requires2FA && !mustChangePassword && (
              <LoginForm
                isLoading={!!isLoading}
                doLogin={handleLogin}
                error={error || undefined}
                turnstile={turnstile}
              />
            )}
          </div>
          <div className="flex flex-col items-center w-full mb-5 text-zinc-700 text-sm gap-2 animate-fade-up dark:text-zinc-200 px-5">
            <a
              href="https://colocapayments.com/terminos/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold cursor-pointer relative inline-block group transition-all duration-300 hover:text-green-500 py-1 dark:hover:text-green-300"
            >
              {t("terms_and_conditions")}
              <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-green-400 dark:bg-green-300 rounded-full transition-all duration-300 group-hover:w-full"></span>
            </a>
            <span className="max-w-lg text-center">
              Coloca Payments - V1.0.0 © {new Date().getFullYear()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
